import type {
  Identified,
  SoftDeletable,
  TenantOwned,
  VersionedEntity
} from "../../shared/kernel/entity.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import type {
  ClientId,
  RdoId,
  RdoConsolidatedId,
  WorkOrderId,
  ServiceFrontId,
  ProfessionalId
} from "../../shared/kernel/identifiers.js";
import type { RdoEntryChangeRecord } from "./rdo-entry-editing.js";

export type RdoStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'ISSUED' | 'CANCELLED';

export interface Rdo extends Identified<RdoId>, TenantOwned, VersionedEntity, SoftDeletable {
  readonly workOrderId: WorkOrderId;
  readonly subWorkOrderId?: string | null;
  /**
   * Frente de Serviço do apontamento — obrigatória em qualquer RDO novo.
   * Continua opcional no tipo apenas para ler registros gravados antes da
   * regra; a criação recusa um RDO sem Frente.
   */
  readonly serviceFrontId: ServiceFrontId | null;
  /** Projeto do apontamento, resolvido a partir da Frente. */
  readonly projectId?: string | null;
  /** Unidade/Área do apontamento, resolvida pela Sub-OS quando inequívoca. */
  readonly unitAreaId?: string | null;
  /** Ordem do apontamento dentro do dia, para a sequência do consolidado. */
  readonly sequence?: number;
  /** Comentário do fiscal, quando houver. */
  readonly inspectorComment?: string | null;
  readonly professionalId: ProfessionalId;
  readonly date: string; // YYYY-MM-DD
  readonly startTime: string; // HH:mm
  readonly endTime: string; // HH:mm
  readonly normalHours: number; // Decimal hours
  readonly overtimeHours: number; // Decimal hours
  readonly om: string | null;
  readonly functionName: string;
  readonly shift: string;
  readonly description: string;
  readonly status: RdoStatus;
  /** Quem criou o apontamento — preservado por toda edição. */
  readonly createdBy?: string | null;
  /** Quem gravou a última edição. */
  readonly updatedBy?: string | null;
  /** Rastro append-only das edições: o que mudou, de quê para quê, por quem. */
  readonly changeHistory?: RdoEntryChangeRecord[];
}

export interface RdoConsolidated extends Identified<RdoConsolidatedId>, TenantOwned, VersionedEntity {
  readonly periodStart: string; // YYYY-MM-DD
  readonly periodEnd: string; // YYYY-MM-DD
  readonly workOrderId?: WorkOrderId | null;
  readonly subWorkOrderId?: string | null;
  readonly serviceFrontId?: ServiceFrontId | null;
  readonly rdoIds: readonly RdoId[];
  readonly totalNormalHours: number;
  readonly totalOvertimeHours: number;
  readonly totalDays: number;
  readonly collaboratorCount: number;
  readonly generatedAt: string;
  readonly generatedBy: string;
  readonly documentNumber?: string;
  readonly documentRevision?: string;
  readonly status?: string;
}

export interface RdoFilterOptions {
  readonly startDate?: string;
  readonly endDate?: string;
  readonly clientId?: string;
  readonly projectId?: string;
  readonly workOrderId?: string;
  readonly subWorkOrderId?: string;
  readonly serviceFrontId?: string;
  readonly professionalId?: string;
  readonly functionName?: string;
  readonly shift?: string;
  readonly status?: string;
}

export interface EnrichedRdo extends Rdo {
  readonly clientName?: string;
  readonly professionalName?: string;
  readonly workOrderCode?: string;
  readonly subWorkOrderCode?: string;
  readonly serviceFrontName?: string;
  /** "CÓDIGO — Nome" do Projeto do apontamento, resolvido pela Frente. */
  readonly projectName?: string;
}

export interface CollaboratorRdoGroup {
  readonly professionalId: string;
  readonly professionalName: string;
  readonly functionName: string;
  readonly rdos: readonly EnrichedRdo[];
  readonly totalDays: number;
  readonly totalNormalHours: number;
  readonly totalOvertimeHours: number;
  readonly periodStart: string;
  readonly periodEnd: string;
}

// Validation logic for RDO hours
export function validateRdoHours(startTime: string, endTime: string, normalHours: number, overtimeHours: number): void {
  if (normalHours < 0 || isNaN(normalHours) || !isFinite(normalHours)) {
    throw new AppError(ErrorCode.Validation, 'Horas normais devem ser um número válido não-negativo');
  }
  if (overtimeHours < 0 || isNaN(overtimeHours) || !isFinite(overtimeHours)) {
    throw new AppError(ErrorCode.Validation, 'Horas extras devem ser um número válido não-negativo');
  }

  const startParts = (startTime || '').split(':').map(Number);
  const endParts = (endTime || '').split(':').map(Number);
  
  const startH = startParts[0] ?? 0;
  const startM = startParts[1] ?? 0;
  const endH = endParts[0] ?? 0;
  const endM = endParts[1] ?? 0;

  if (startH < 0 || startH > 23 || startM < 0 || startM > 59) {
    throw new AppError(ErrorCode.Validation, 'Horário inicial inválido (use formato HH:mm)');
  }
  if (endH < 0 || endH > 23 || endM < 0 || endM > 59) {
    throw new AppError(ErrorCode.Validation, 'Horário final inválido (use formato HH:mm)');
  }

  const startTotalMinutes = startH * 60 + startM;
  let endTotalMinutes = endH * 60 + endM;

  // Handle cross-day shift (e.g. 22:00 to 06:00)
  if (endTotalMinutes < startTotalMinutes) {
    endTotalMinutes += 24 * 60;
  }

  const diffMinutes = endTotalMinutes - startTotalMinutes;
  const diffHours = diffMinutes / 60;
  const claimedTotal = normalHours + overtimeHours;

  if (claimedTotal > diffHours + 0.05) { // Small tolerance for float rounding
    throw new AppError(ErrorCode.Validation, `Soma de horas normais (${normalHours}h) e extras (${overtimeHours}h) excede o tempo total do turno (${diffHours.toFixed(2)}h)`);
  }
}
